import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import Player from './components/Player';
import AdminPanel from './components/AdminPanel';
import LogoutButton from './components/LogoutButton';
import Footer from './components/Footer';
import { mockVibes, mockPlaylists } from './mock';
import { songsAPI, vibesAPI } from './services/api';
import { apiCache } from './utils/apiCache';

function App() {
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isShuffled, setIsShuffled] = useState(false);
  const [repeatMode, setRepeatMode] = useState('off'); // 'off', 'all', 'one'
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [likedSongs, setLikedSongs] = useState(new Set());
  const [user, setUser] = useState(null); // Will be set when logged in
  const [isAdmin, setIsAdmin] = useState(false);
  const [songs, setSongs] = useState([]);
  const [vibes, setVibes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Ad integration state
  const [songCounter, setSongCounter] = useState(0);
  const [isPlayingAd, setIsPlayingAd] = useState(false);
  const [pendingSong, setPendingSong] = useState(null);

  // Process Google OAuth session_id from URL fragment
  useEffect(() => {
    const processSessionId = async () => {
      // Check for session_id in URL fragment
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');
        
        if (sessionId) {
          console.log('🔑 Processing Google OAuth session...');
          setLoading(true);
          
          try {
            // Call backend to process session_id
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/session`, {
              method: 'POST',
              headers: {
                'X-Session-ID': sessionId
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              console.log('✅ Google OAuth successful:', data);
              
              // Store session token in cookie (httpOnly would be set by backend in production)
              document.cookie = `session_token=${data.session_token}; path=/; max-age=${7 * 24 * 60 * 60}; secure; samesite=none`;
              
              // Store user in localStorage
              const userData = {
                id: data.id,
                email: data.email,
                name: data.name,
                picture: data.picture,
                is_admin: data.is_admin || false
              };
              localStorage.setItem('user', JSON.stringify(userData));
              localStorage.setItem('auth_token', data.session_token);
              
              // Set user state
              setUser(userData);
              setIsAdmin(userData.is_admin);
              
              // Clean URL fragment
              window.history.replaceState(null, '', window.location.pathname);
            } else {
              console.error('❌ Failed to process session');
              alert('Failed to sign in with Google. Please try again.');
            }
          } catch (error) {
            console.error('❌ Error processing session:', error);
            alert('Network error during Google sign in. Please try again.');
          } finally {
            setLoading(false);
          }
        }
      }
    };
    
    processSessionId();
  }, []);

  // Restore user session on app load
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('Restoring user:', userData);
        setUser(userData);
        setIsAdmin(userData.is_admin === true);
        console.log('Admin status set to:', userData.is_admin);
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
      }
    }
  }, []);

  // Function to fetch data (used on mount and for refreshing)
  const fetchData = async (skipCache = false) => {
    try {
      setLoading(true);
      
      // Try cache first (unless explicitly skipped)
      if (!skipCache) {
        const cachedSongs = apiCache.get('songs');
        const cachedVibes = apiCache.get('vibes');
        
        if (cachedSongs && cachedVibes) {
          console.log('⚡ Using cached data');
          setSongs(cachedSongs);
          setVibes(cachedVibes);
          setLoading(false);
          return;
        }
      }
      
      console.log('🎵 Fetching data from API...');
      
      // Fetch songs and vibes in parallel
      const [fetchedSongs, fetchedVibes] = await Promise.all([
        songsAPI.getSongs(),
        vibesAPI.getVibes()
      ]);
      
      console.log('✅ Fetched songs:', fetchedSongs.length);
      console.log('✅ Fetched vibes:', fetchedVibes.length);
      console.log('📊 Raw vibe data:', fetchedVibes.map(v => ({name: v.name, song_count: v.song_count})));
      
      // Transform vibes data and calculate actual song counts
      const vibesWithCounts = fetchedVibes.map(vibe => {
        const calculatedCount = fetchedSongs.filter(song => 
          song.vibes && song.vibes.includes(vibe.id)
        ).length;
        const finalCount = vibe.song_count || calculatedCount;
        console.log(`🎵 Vibe "${vibe.name}": backend=${vibe.song_count}, calculated=${calculatedCount}, final=${finalCount}`);
        return {
          ...vibe,
          songCount: finalCount
        };
      });
      
      console.log('✅ Processed vibes:', vibesWithCounts);
      
      // Cache the results (5 minutes TTL)
      apiCache.set('songs', fetchedSongs, 5 * 60 * 1000);
      apiCache.set('vibes', vibesWithCounts, 5 * 60 * 1000);
        
      setSongs(fetchedSongs);
      setVibes(vibesWithCounts);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Fallback to mock data
      setSongs([]);
      setVibes(mockVibes);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  // Debug logging for admin status changes
  useEffect(() => {
    console.log('isAdmin state changed to:', isAdmin);
    console.log('user state:', user);
  }, [isAdmin, user]);

  const playSong = async (song, songList = []) => {
    console.log('🎵 playSong called with:', song);
    console.log('🎵 Song has audioUrl:', song?.audioUrl, 'albumArt:', song?.albumArt);
    
    // Don't count ads as songs
    if (!song?.isAd && !isPlayingAd) {
      const newCounter = songCounter + 1;
      console.log(`📊 Song Counter: ${songCounter} → ${newCounter}`);
      setSongCounter(newCounter);
      
      // Check if we need to play an ad (every 10 songs)
      if (newCounter >= 10) {
        console.log('📢 🎯 AD TRIGGER! Counter reached 10. Fetching ad...');
        setPendingSong(songList.length > 0 ? {song, songList} : {song});
        
        try {
          const timestamp = new Date().toISOString();
          console.log(`📢 [${timestamp}] Fetching random ad from: ${process.env.REACT_APP_BACKEND_URL}/api/ads/random`);
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/random`);
          if (response.ok) {
            const ad = await response.json();
            console.log(`📢 ✅ Ad fetched successfully - Title: "${ad.title}", ID: ${ad.id}`);
            console.log('📢 Full ad object:', JSON.stringify(ad, null, 2));
            
            // Mark as ad and play
            setIsPlayingAd(true);
            setCurrentSong({
              ...ad,
              id: ad.id,
              title: ad.title,
              artist: 'Advertisement',
              album: 'Ad',
              audioUrl: ad.audioUrl,
              albumArt: ad.imageUrl || 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=300&h=300&fit=crop',
              websiteUrl: ad.websiteUrl,
              isAd: true
            });
            setIsPlaying(true);
            
            // Track ad play (fire and forget)
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${ad.id}/track`, {
              method: 'POST'
            }).catch(err => console.log('Failed to track ad'));
            
            // Reset counter
            console.log('📢 Counter reset to 0');
            setSongCounter(0);
            return;
          } else {
            console.log('❌ Failed to fetch ad - status:', response.status);
          }
        } catch (error) {
          console.log('❌ Error fetching ad:', error);
          // Continue to play song if ad fetch fails
        }
      }
    }
    
    // Play regular song
    console.log('🎵 Playing song:', song.title);
    setCurrentSong(song);
    if (songList.length > 0) {
      setQueue(songList);
      setCurrentIndex(songList.findIndex(s => s.id === song.id));
    }
    setIsPlaying(true);
    
    // Track play for recommendations (async, don't wait)
    if (song && song.id) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/listening-history/track?song_id=${song.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      }).catch(err => console.log('Failed to track play:', err));
    }
  };

  const toggleLike = (songId) => {
    if (!user) {
      if (window.confirm('Login or create a free account to use this feature. Go to login page?')) {
        window.location.href = '/login';
      }
      return;
    }
    const newLikedSongs = new Set(likedSongs);
    if (newLikedSongs.has(songId)) {
      newLikedSongs.delete(songId);
    } else {
      newLikedSongs.add(songId);
    }
    setLikedSongs(newLikedSongs);
  };

  const nextSong = async () => {
    if (queue.length === 0) return;
    
    // Increment counter and check for ad (only if not currently playing an ad)
    if (!isPlayingAd && !currentSong?.isAd) {
      const newCounter = songCounter + 1;
      console.log(`📊 [nextSong] Song Counter: ${songCounter} → ${newCounter}`);
      setSongCounter(newCounter);
      
      // Check if we need to play an ad (every 10 songs)
      if (newCounter >= 10) {
        console.log('📢 🎯 [nextSong] AD TRIGGER! Counter reached 10. Fetching ad...');
        
        // Determine what the next song would be
        let nextIndex;
        if (isShuffled) {
          nextIndex = Math.floor(Math.random() * queue.length);
        } else {
          nextIndex = currentIndex + 1;
          if (nextIndex >= queue.length) {
            nextIndex = 0;
          }
        }
        
        setPendingSong({ song: queue[nextIndex], songList: queue });
        
        try {
          const timestamp = new Date().toISOString();
          console.log(`📢 [${timestamp}] [nextSong] Fetching random ad from: ${process.env.REACT_APP_BACKEND_URL}/api/ads/random`);
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/random`);
          if (response.ok) {
            const ad = await response.json();
            console.log(`📢 ✅ [nextSong] Ad fetched successfully - Title: "${ad.title}", ID: ${ad.id}`);
            console.log('📢 [nextSong] Full ad object:', JSON.stringify(ad, null, 2));
            
            // Mark as ad and play
            setIsPlayingAd(true);
            setCurrentSong({
              ...ad,
              id: ad.id,
              title: ad.title,
              artist: 'Advertisement',
              album: 'Ad',
              audioUrl: ad.audioUrl,
              albumArt: ad.imageUrl || 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=300&h=300&fit=crop',
              websiteUrl: ad.websiteUrl,
              isAd: true
            });
            setIsPlaying(true);
            
            // Track ad play
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${ad.id}/track`, {
              method: 'POST'
            }).catch(err => console.log('Failed to track ad'));
            
            // Reset counter
            console.log('📢 [nextSong] Counter reset to 0');
            setSongCounter(0);
            return;
          } else {
            console.log('❌ [nextSong] Failed to fetch ad - status:', response.status);
          }
        } catch (error) {
          console.log('❌ [nextSong] Error fetching ad:', error);
        }
      }
    }
    
    // Original nextSong logic continues unchanged
    let nextIndex;
    if (isShuffled) {
      nextIndex = Math.floor(Math.random() * queue.length);
    } else {
      nextIndex = currentIndex + 1;
      
      // If we've reached the end of the queue, play a random song from the same genre
      if (nextIndex >= queue.length) {
        if (currentSong && currentSong.genre) {
          // Filter songs by same genre
          const sameGenreSongs = songs.filter(s => 
            s.genre === currentSong.genre && s.id !== currentSong.id
          );
          
          if (sameGenreSongs.length > 0) {
            // Pick a random song from the same genre
            const randomSong = sameGenreSongs[Math.floor(Math.random() * sameGenreSongs.length)];
            setCurrentSong(randomSong);
            setIsPlaying(true);
            return;
          }
        }
        // If no songs from same genre, loop back to start
        nextIndex = 0;
      }
    }
    
    setCurrentIndex(nextIndex);
    setCurrentSong(queue[nextIndex]);
    setIsPlaying(true);
  };

  const handleAdComplete = () => {
    console.log('📢 Ad finished, playing pending song');
    setIsPlayingAd(false);
    
    if (pendingSong) {
      const { song, songList } = pendingSong;
      setPendingSong(null);
      playSong(song, songList || []);
    } else {
      // If no pending song, just continue with next in queue
      nextSong();
    }
  };

  const previousSong = async () => {
    if (queue.length === 0) return;
    
    // Increment counter and check for ad (only if not currently playing an ad)
    if (!isPlayingAd && !currentSong?.isAd) {
      const newCounter = songCounter + 1;
      console.log(`📊 [previousSong] Song Counter: ${songCounter} → ${newCounter}`);
      setSongCounter(newCounter);
      
      // Check if we need to play an ad (every 10 songs)
      if (newCounter >= 10) {
        console.log('📢 🎯 [previousSong] AD TRIGGER! Counter reached 10. Fetching ad...');
        
        // Determine what the previous song would be
        let prevIndex;
        if (isShuffled) {
          prevIndex = Math.floor(Math.random() * queue.length);
        } else {
          prevIndex = currentIndex === 0 ? queue.length - 1 : currentIndex - 1;
        }
        
        setPendingSong({ song: queue[prevIndex], songList: queue });
        
        try {
          const timestamp = new Date().toISOString();
          console.log(`📢 [${timestamp}] [previousSong] Fetching random ad from: ${process.env.REACT_APP_BACKEND_URL}/api/ads/random`);
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/random`);
          if (response.ok) {
            const ad = await response.json();
            console.log(`📢 ✅ [previousSong] Ad fetched successfully - Title: "${ad.title}", ID: ${ad.id}`);
            console.log('📢 [previousSong] Full ad object:', JSON.stringify(ad, null, 2));
            
            // Mark as ad and play
            setIsPlayingAd(true);
            setCurrentSong({
              ...ad,
              id: ad.id,
              title: ad.title,
              artist: 'Advertisement',
              album: 'Ad',
              audioUrl: ad.audioUrl,
              albumArt: ad.imageUrl || 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=300&h=300&fit=crop',
              websiteUrl: ad.websiteUrl,
              isAd: true
            });
            setIsPlaying(true);
            
            // Track ad play
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads/${ad.id}/track`, {
              method: 'POST'
            }).catch(err => console.log('Failed to track ad'));
            
            // Reset counter
            console.log('📢 [previousSong] Counter reset to 0');
            setSongCounter(0);
            return;
          } else {
            console.log('❌ [previousSong] Failed to fetch ad - status:', response.status);
          }
        } catch (error) {
          console.log('❌ [previousSong] Error fetching ad:', error);
        }
      }
    }
    
    // Original previousSong logic continues unchanged
    let prevIndex;
    if (isShuffled) {
      prevIndex = Math.floor(Math.random() * queue.length);
    } else {
      prevIndex = currentIndex === 0 ? queue.length - 1 : currentIndex - 1;
    }
    
    setCurrentIndex(prevIndex);
    setCurrentSong(queue[prevIndex]);
    setIsPlaying(true);
  };

  const handleLogout = () => {
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Reload page to reset all state
    window.location.href = '/';
  };

  return (
    <Router>
      <div className="app">
        <div className="app-content">
          <Sidebar user={user} isAdmin={isAdmin} onLogout={handleLogout} />
          <div className="main-view">
            <Routes>
              <Route path="/admin/*" element={
                <AdminPanel 
                  songs={songs} 
                  vibes={vibes}
                  isAdmin={isAdmin}
                  user={user}
                  refreshSongs={fetchData}
                />
              } />
              <Route path="/*" element={
                <MainContent 
                  playSong={playSong}
                  songs={songs}
                  vibes={vibes}
                  playlists={mockPlaylists}
                  likedSongs={likedSongs}
                  toggleLike={toggleLike}
                  user={user}
                  setUser={setUser}
                  setIsAdmin={setIsAdmin}
                  loading={loading}
                  onLogout={handleLogout}
                />
              } />
            </Routes>
          </div>
        </div>
        <Player 
          currentSong={currentSong}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          currentTime={currentTime}
          setCurrentTime={setCurrentTime}
          duration={duration}
          setDuration={setDuration}
          volume={volume}
          setVolume={setVolume}
          isShuffled={isShuffled}
          setIsShuffled={setIsShuffled}
          repeatMode={repeatMode}
          setRepeatMode={setRepeatMode}
          nextSong={nextSong}
          previousSong={previousSong}
          likedSongs={likedSongs}
          toggleLike={toggleLike}
          playSong={playSong}
          user={user}
          isPlayingAd={isPlayingAd}
          onAdComplete={handleAdComplete}
        />
        <Footer />
      </div>
    </Router>
  );
}

export default App;